import React from 'react';
import { api, ApiError } from '../../api';

const HOME = '/group';
/** Where to go after signing in: only a path inside LEO CORE (one leading '/', same origin). Anything else — a
 *  `javascript:` URL, another site, `//host`, `/\host` — goes to the home page instead. */
export function safeNext(raw: string | null, origin: string = location.origin): string {
  if (!raw || raw[0] !== '/' || raw[1] === '/' || raw[1] === '\\' || /[\u0000-\u001f\\]/.test(raw)) return HOME;
  try { const u = new URL(raw, origin); return u.origin === origin ? u.pathname + u.search + u.hash : HOME; } catch { return HOME; }
}

/** The sign-in portal. One form; errors in plain words; never says whether the email exists. */
export function Login() {
  const [email, setEmail] = React.useState(''); const [password, setPassword] = React.useState(''); const [err, setErr] = React.useState<string | null>(null); const [busy, setBusy] = React.useState(false);
  const [setupOpen, setSetupOpen] = React.useState(false);
  React.useEffect(() => { api<{ open: boolean }>('/api/v1/auth/setup').then(r => setSetupOpen(r.open)).catch(() => undefined); }, []);
  const next = safeNext(new URLSearchParams(location.search).get('next'));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try { const r = await api<{ mustChangePassword: boolean }>('/api/v1/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }); location.assign(r.mustChangePassword ? '/password' : next); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not sign in just now.'); setBusy(false); }
  };
  return (
    <AuthFrame title="Sign in" sub="LEO CORE · The intelligence behind every decision">
      <form onSubmit={submit} className="authform">
        <label>Email<input type="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} required autoFocus /></label>
        <label>Password<input type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} required /></label>
        {err && <div className="callout">{err}</div>}
        <button className="btn primary" disabled={busy || !email || !password}>{busy ? 'Signing in…' : 'Sign in'}</button>
        <p className="muted small">Forgotten your password? Ask an administrator to reset it; you will receive a temporary one to change on first sign-in.</p>
        {setupOpen && <p className="small"><a className="link" href="/setup">First-time setup</a> — this instance has no owner account yet.</p>}
      </form>
    </AuthFrame>
  );
}

export function Setup() {
  const [f, setF] = React.useState({ token: '', email: '', name: '', password: '', confirm: '' }); const [err, setErr] = React.useState<string | null>(null); const [busy, setBusy] = React.useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF(x => ({ ...x, [k]: e.target.value }));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(null); if (f.password !== f.confirm) { setErr('The two passwords do not match.'); return; } setBusy(true);
    try { await api('/api/v1/auth/setup', { method: 'POST', body: JSON.stringify({ token: f.token.trim(), email: f.email, name: f.name, password: f.password }) }); location.assign('/group'); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Setup failed.'); setBusy(false); }
  };
  return (
    <AuthFrame title="First-time setup" sub="Create the owner account. The setup token was printed once in the server log when this instance first started.">
      <form onSubmit={submit} className="authform">
        <label>Setup token<input value={f.token} onChange={set('token')} required autoFocus /></label>
        <label>Your name<input value={f.name} onChange={set('name')} required /></label>
        <label>Email<input type="email" value={f.email} onChange={set('email')} required /></label>
        <label>Password<input type="password" autoComplete="new-password" value={f.password} onChange={set('password')} required /><span className="muted small">At least 10 characters; a short sentence works best.</span></label>
        <label>Confirm password<input type="password" autoComplete="new-password" value={f.confirm} onChange={set('confirm')} required /></label>
        {err && <div className="callout">{err}</div>}
        <button className="btn primary" disabled={busy}>{busy ? 'Setting up…' : 'Create the owner account'}</button>
      </form>
    </AuthFrame>
  );
}

export function ChangePassword({ forced }: { forced?: boolean }) {
  const [f, setF] = React.useState({ current: '', next: '', confirm: '' }); const [err, setErr] = React.useState<string | null>(null); const [ok, setOk] = React.useState(false); const [busy, setBusy] = React.useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF(x => ({ ...x, [k]: e.target.value }));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(null); if (f.next !== f.confirm) { setErr('The two new passwords do not match.'); return; } setBusy(true);
    try { await api('/api/v1/auth/change-password', { method: 'POST', body: JSON.stringify({ current: f.current, next: f.next }) }); setOk(true); setTimeout(() => location.assign('/group'), 800); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not change the password.'); setBusy(false); }
  };
  return (
    <AuthFrame title={forced ? 'Choose your password' : 'Change password'} sub={forced ? 'You signed in with a temporary password. Choose your own before continuing.' : 'Changing your password signs you out everywhere else.'}>
      <form onSubmit={submit} className="authform">
        <label>{forced ? 'Temporary password' : 'Current password'}<input type="password" autoComplete="current-password" value={f.current} onChange={set('current')} required autoFocus /></label>
        <label>New password<input type="password" autoComplete="new-password" value={f.next} onChange={set('next')} required /><span className="muted small">At least 10 characters; a short sentence works best.</span></label>
        <label>Confirm new password<input type="password" autoComplete="new-password" value={f.confirm} onChange={set('confirm')} required /></label>
        {err && <div className="callout">{err}</div>}
        {ok && <div className="callout info">Password changed.</div>}
        <button className="btn primary" disabled={busy}>{busy ? 'Saving…' : 'Save password'}</button>
      </form>
    </AuthFrame>
  );
}

function AuthFrame({ title, sub, children }: { title: string; sub: string; children: React.ReactNode }) {
  return (
    <div className="authpage">
      <div className="authcard">
        <div className="brand"><div className="mark">L</div><div><div className="name">LEO CORE</div><div className="sub">Leo Group</div></div></div>
        <h1>{title}</h1><p className="lead">{sub}</p>
        {children}
      </div>
    </div>
  );
}
